import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { firmApi } from "../services/api";
import { TOUR_STEPS } from "../components/guided-tour/tourSteps";

// ── Types ──────────────────────────────────────────────────────────────

interface GuidedTourState {
  isActive: boolean;
  currentStep: number;
  createdMatterId: string | null;
  createdClientId: string | null;
  clientDisplayName: string | null;
  matterTitle: string | null;
}

interface GuidedTourContextValue {
  tour: GuidedTourState;
  advanceStep: () => void;
  setCreatedEntities: (data: {
    matterId: string;
    clientId: string;
    clientDisplayName: string;
    matterTitle: string;
  }) => void;
  completeTour: () => Promise<void>;
  startTour: () => void;
  /** Start the tour again even if it was completed or skipped before */
  restartTour: () => void;
  isPaused: boolean;
  setPaused: (paused: boolean) => void;
}

const STORAGE_KEY = "kavoa_guided_tour";
const COMPLETED_KEY = "kavoa_guided_tour_completed";
// Set when the user restarts a finished tour, so the backend "completed"
// flag doesn't cancel it on reload
const RESTARTED_KEY = "kavoa_guided_tour_restarted";

// ── Helpers ────────────────────────────────────────────────────────────

function loadState(): GuidedTourState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    isActive: false,
    currentStep: 0,
    createdMatterId: null,
    createdClientId: null,
    clientDisplayName: null,
    matterTitle: null,
  };
}

function saveState(state: GuidedTourState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// ── Context ────────────────────────────────────────────────────────────

const GuidedTourContext = createContext<GuidedTourContextValue | undefined>(undefined);

export const useGuidedTour = (): GuidedTourContextValue => {
  const ctx = useContext(GuidedTourContext);
  if (!ctx) throw new Error("useGuidedTour must be used within GuidedTourProvider");
  return ctx;
};

// ── Provider ───────────────────────────────────────────────────────────

export const GuidedTourProvider: React.FC<{ isLoggedIn: boolean; onboardingComplete: boolean; children: React.ReactNode }> = ({ isLoggedIn, onboardingComplete, children }) => {
  const [tour, setTour] = useState<GuidedTourState>(loadState);
  const [isPaused, setPaused] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Persist state changes to localStorage (only when tour is active)
  useEffect(() => {
    if (tour.isActive) {
      saveState(tour);
    }
  }, [tour]);

  // Handle auth state changes  -  reset on logout, check backend on login.
  // The tour must not start until the firm onboarding wizard is complete.
  useEffect(() => {
    if (!isLoggedIn) {
      // Logged out  -  deactivate tour and clear persisted state
      setTour({
        isActive: false,
        currentStep: 0,
        createdMatterId: null,
        createdClientId: null,
        clientDisplayName: null,
        matterTitle: null,
      });
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(COMPLETED_KEY);
      localStorage.removeItem(RESTARTED_KEY);
      return;
    }

    // Don't start the guided tour until the firm has finished onboarding
    if (!onboardingComplete) return;

    // Logged in and onboarded  -  check backend for tour status
    if (localStorage.getItem(COMPLETED_KEY) === "true") return;
    if (localStorage.getItem(RESTARTED_KEY) === "true") return;

    firmApi.getGuidedTourStatus().then((res) => {
      if (res.data?.completed) {
        // Backend says completed, sync localStorage and clear any stale active state
        localStorage.setItem(COMPLETED_KEY, "true");
        localStorage.removeItem(STORAGE_KEY);
        setTour((prev) => ({ ...prev, isActive: false }));
      } else {
        // Not completed  -  auto-start if not already active
        setTour((prev) => {
          if (prev.isActive) return prev;
          return {
            isActive: true,
            currentStep: 0,
            createdMatterId: null,
            createdClientId: null,
            clientDisplayName: null,
            matterTitle: null,
          };
        });
      }
    }).catch(() => {});
  }, [isLoggedIn, onboardingComplete]);

  // Navigation-based step advancement
  useEffect(() => {
    if (!tour.isActive) return;

    // Don't redirect while the firm onboarding wizard is active  -  would
    // create an infinite loop with the App-level catch-all redirect.
    if (location.pathname === "/onboarding") return;

    const step = TOUR_STEPS[tour.currentStep];
    if (!step) return;

    // Step 0: advance when user reaches /matters
    if (tour.currentStep === 0 && location.pathname === "/matters") {
      setTour((prev) => ({ ...prev, currentStep: 1 }));
      return;
    }

    // Redirect back to required path if user somehow navigates away
    if (step.requiredPath && location.pathname !== step.requiredPath) {
      const timer = setTimeout(() => {
        if (step.requiredPath && location.pathname !== step.requiredPath) {
          navigate(step.requiredPath, { replace: true });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tour.isActive, tour.currentStep, location.pathname, navigate]);

  const startTour = useCallback(() => {
    // Don't start if already completed
    if (localStorage.getItem(COMPLETED_KEY) === "true") return;
    setTour({
      isActive: true,
      currentStep: 0,
      createdMatterId: null,
      createdClientId: null,
      clientDisplayName: null,
      matterTitle: null,
    });
    setPaused(false);
  }, []);

  const restartTour = useCallback(() => {
    localStorage.removeItem(COMPLETED_KEY);
    localStorage.setItem(RESTARTED_KEY, "true");
    setPaused(false);
    setTour({
      isActive: true,
      currentStep: 0,
      createdMatterId: null,
      createdClientId: null,
      clientDisplayName: null,
      matterTitle: null,
    });
    // Step 0 starts on the dashboard
    navigate("/");
  }, [navigate]);

  const advanceStep = useCallback(() => {
    setTour((prev) => {
      if (prev.currentStep >= TOUR_STEPS.length - 1) return prev;
      return { ...prev, currentStep: prev.currentStep + 1 };
    });
  }, []);

  const setCreatedEntities = useCallback(
    (data: { matterId: string; clientId: string; clientDisplayName: string; matterTitle: string }) => {
      setTour((prev) => ({
        ...prev,
        createdMatterId: data.matterId,
        createdClientId: data.clientId,
        clientDisplayName: data.clientDisplayName,
        matterTitle: data.matterTitle,
      }));
    },
    []
  );

  const completeTour = useCallback(async () => {
    setTour((prev) => ({ ...prev, isActive: false }));
    setPaused(false);
    localStorage.setItem(COMPLETED_KEY, "true");
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(RESTARTED_KEY);
    // Also suppress the old WelcomeTutorial
    localStorage.setItem("kavoa_tutorial_completed", "true");
    try {
      await firmApi.completeGuidedTour();
    } catch {
      // Non-critical  -  localStorage is the fast check
    }
  }, []);

  return (
    <GuidedTourContext.Provider
      value={{ tour, advanceStep, setCreatedEntities, completeTour, startTour, restartTour, isPaused, setPaused }}
    >
      {children}
    </GuidedTourContext.Provider>
  );
};
